import React, { useEffect } from 'react'
import { useAtom } from 'jotai'
import { Typography } from '../../ui/Typography/Typography'
import { Button } from '../../ui/Button/Button'

import {
    closedCasesListAtom,
    fetchClosedCasesAtom,
    fetchClosedCasesWithPageAtom,
    isFetchingClosedCasesAtom,
    isFetchingNextClosedCasesPageAtom,
    sortedClosedCasesAtom,
} from '../../../store/filter/filterCasesAtom'
// Import Swiper styles
import { formatDate } from '../../../utils/format'
import {
    getCaseStatusColorCode,
    getCaseStatusLabel,
    getUpdateStatusLabel,
} from '../../../utils/utils'
import { Badge } from '../../ui/Badge/Badge'
import {
    Card,
    CardCell,
    CardContentMobile,
    CardHeader,
    CardHeaderCell,
    CardList,
    CardMobile,
    Circle,
    CircleText,
} from '../../ui/Card'

import { Case } from '../../../models/Case'
import { SortCasesButton as SortButton } from '../../ui/SortButton'
import { L } from '../../providers/Lokalise'
import { SortCasesProperties } from '../../../utils/sortCasesHelper'
import useSortCasesList from '../../../hooks/useSortCasesList'
import { CaseCardSkeleton, CaseCardSkeletonMobile } from './OpenCases'
import { colors } from '../../../theme/palette'
import { getInitials } from '../../../utils/initials'

// Should be 8 columns out of 100%
const gridTemplateColumns = '14% 20% 21% 21% 12% 12%'

interface ClosedCasesProps {
    cases: Case[]
}

const ClosedCases: React.FC<ClosedCasesProps> = (): JSX.Element => {
    const [, fetchClosedCases] = useAtom(fetchClosedCasesAtom)
    const [, fetchClosedCasesWithPage] = useAtom(fetchClosedCasesWithPageAtom)
    const [closedCasesList] = useAtom(closedCasesListAtom)
    const [isFetchingClosedCases] = useAtom(isFetchingClosedCasesAtom)
    const [isFetchingNextClosedCasesPage] = useAtom(
        isFetchingNextClosedCasesPageAtom
    )
    const [, setSortedClosedCases] = useAtom(sortedClosedCasesAtom)
    const { setSort, sortState } = useSortCasesList('closed')

    const handlePageChange = (page: number): void => {
        fetchClosedCasesWithPage(page)
    }

    useEffect(() => {
        fetchClosedCases()
    }, [fetchClosedCases])

    useEffect(() => {
        localStorage.setItem('salary-last-tab', 'closed')
    }, [])

    // Reset sort state to default when component unmounts
    useEffect(() => {
        return () => {
            setSortedClosedCases({
                property: 'Default',
                direction: 'Default',
            })
        }
    }, [setSortedClosedCases])

    const handleSort = (property: string) => (event: React.MouseEvent) => {
        event.preventDefault()
        setSort(property as SortCasesProperties)
        fetchClosedCases()
    }

    if (closedCasesList.cases.length === 0 && !isFetchingClosedCases) {
        return (
            <div
                style={{
                    padding: '2rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                }}
            >
                <Typography size="S">
                    {L('LØN_AFSLUTTET_SAGER_INGEN_SAGER_LABEL')}
                </Typography>
            </div>
        )
    }

    return (
        <>
            <CardList>
                <CardHeader gridTemplateColumns={gridTemplateColumns}>
                    <CardHeaderCell>
                        <SortButton
                            onClick={handleSort('ModtagetDato')}
                            sort={sortState}
                            name="ModtagetDato"
                        >
                            {L(
                                'LØN_ÅBNE_SAGER_TABEL_KOLONNETITEL_MODATGET_DATO'
                            )}
                        </SortButton>
                    </CardHeaderCell>
                    <CardHeaderCell>
                        <SortButton
                            onClick={handleSort('NyansatteNavn')}
                            sort={sortState}
                            name="NyansatteNavn"
                        >
                            {L(
                                'LØN_ÅBNE_SAGER_TABEL_KOLONNETITEL_NYANSATTES_NAVN'
                            )}
                        </SortButton>
                    </CardHeaderCell>
                    <CardHeaderCell>
                        <Typography size="XS" weight="bold">
                            <SortButton
                                onClick={handleSort('ArbejdsstedNavn')}
                                sort={sortState}
                                name="ArbejdsstedNavn"
                            >
                                {L(
                                    'LØN_ÅBNE_SAGER_TABEL_KOLONNETITEL_ARBEJDSSTED'
                                )}
                            </SortButton>
                        </Typography>
                    </CardHeaderCell>
                    <CardHeaderCell>
                        <Typography size="XS" weight="bold">
                            <SortButton
                                onClick={handleSort('StillingNavn')}
                                sort={sortState}
                                name="StillingNavn"
                            >
                                {L(
                                    'LØN_ÅBNE_SAGER_TABEL_KOLONNETITEL_STILLING'
                                )}
                            </SortButton>
                        </Typography>
                    </CardHeaderCell>
                    <CardHeaderCell>
                        <SortButton
                            onClick={handleSort('StatusMMNavn')}
                            sort={sortState}
                            name="StatusMMNavn"
                        >
                            {L('LØN_ÅBNE_SAGER_TABEL_KOLONNETITEL_STATUS')}
                        </SortButton>
                    </CardHeaderCell>
                    <CardHeaderCell>
                        <SortButton
                            onClick={handleSort('FristDato')}
                            sort={sortState}
                            name="FristDato"
                        >
                            {L(
                                'LØN_AFSLUTTET_SAGER_TABEL_KOLONNETITEL_AFSLUTTET_DATO'
                            )}
                        </SortButton>
                    </CardHeaderCell>
                </CardHeader>
                {isFetchingClosedCases && (
                    <>
                        {Array.from({ length: 10 }).map((_) => (
                            <>
                                <CaseCardSkeleton
                                    key={`skeleton-${Math.random()
                                        .toString(36)
                                        .substr(2, 9)}`}
                                />
                                <CaseCardSkeletonMobile
                                    key={`skeleton-${Math.random()
                                        .toString(36)
                                        .substr(2, 9)}`}
                                />
                            </>
                        ))}
                    </>
                )}
                {!isFetchingClosedCases &&
                    closedCasesList.cases.map((caseItem) => {
                        return (
                            <div key={caseItem.id}>
                                <Card
                                    key={caseItem.id}
                                    gridTemplateColumns={gridTemplateColumns}
                                    url={`/mine-lonindplaceringer/sag/${caseItem.caseId}`}
                                >
                                    <CardCell>
                                        <Typography size="XS">
                                            {formatDate(
                                                new Date(caseItem.receivedDate)
                                            )}
                                        </Typography>
                                    </CardCell>
                                    <CardCell paddingSize="small">
                                        <Typography size="XS">
                                            {caseItem.newlyHired?.name}
                                        </Typography>
                                    </CardCell>
                                    <CardCell paddingSize="small">
                                        <Typography size="XS">
                                            {
                                                caseItem.newlyHired?.workplace
                                                    ?.navn
                                            }
                                        </Typography>
                                    </CardCell>
                                    <CardCell paddingSize="small">
                                        <Typography size="XS">
                                            {
                                                caseItem.newlyHired?.position
                                                    ?.navn
                                            }
                                        </Typography>
                                    </CardCell>
                                    <CardCell paddingSize="small">
                                        {caseItem.caseStatus?.statusNavn.toLocaleLowerCase() !==
                                        'ikkeopdateret' ? (
                                            <Badge
                                                backgroundColor={
                                                    getCaseStatusColorCode(
                                                        caseItem.caseStatus
                                                            ?.statusType
                                                    ).bgColor
                                                }
                                                color={
                                                    getCaseStatusColorCode(
                                                        caseItem.caseStatus
                                                            ?.statusType
                                                    ).color
                                                }
                                            >
                                                {caseItem.caseStatus
                                                    ?.statusNavn ?? ''}
                                            </Badge>
                                        ) : null}
                                    </CardCell>
                                    <CardCell paddingSize="small">
                                        {caseItem?.caseClosedDate &&
                                        caseItem?.caseClosedDate !== null
                                            ? formatDate(
                                                  new Date(
                                                      caseItem.caseClosedDate
                                                  )
                                              )
                                            : ''}
                                    </CardCell>
                                </Card>
                                <CardMobile
                                    url={`/mine-lonindplaceringer/sag/${caseItem.caseId}`}
                                >
                                    <CardCell>
                                        <Circle>
                                            <CircleText>
                                                {getInitials(
                                                    caseItem?.newlyHired
                                                        ?.name ?? '??'
                                                )}
                                            </CircleText>
                                        </Circle>
                                    </CardCell>
                                    <CardContentMobile>
                                        <CardCell>
                                            <Typography size="XS">
                                                {formatDate(
                                                    new Date(
                                                        caseItem.receivedDate
                                                    )
                                                )}
                                            </Typography>
                                            <Typography size="XS">
                                                {caseItem.newlyHired?.name}
                                            </Typography>
                                            <Typography size="XS">
                                                {
                                                    caseItem.newlyHired
                                                        ?.position?.navn
                                                }
                                            </Typography>
                                            <Typography size="XS">
                                                {
                                                    caseItem.newlyHired
                                                        ?.workplace?.navn
                                                }
                                            </Typography>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                }}
                                            >
                                                Afsluttet:
                                                <Typography size="XS">
                                                    {formatDate(
                                                        new Date(
                                                            caseItem.caseClosedDate
                                                        )
                                                    )}
                                                </Typography>
                                            </div>
                                        </CardCell>
                                    </CardContentMobile>
                                </CardMobile>
                            </div>
                        )
                    })}
            </CardList>
            {!isFetchingClosedCases &&
                closedCasesList.totalSize > closedCasesList.cases.length && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: '3rem',
                            marginBottom: '3rem',
                        }}
                    >
                        <Button
                            onClick={() =>
                                handlePageChange(closedCasesList.page + 1)
                            }
                            type="primary"
                            size="xlarge"
                            loading={isFetchingNextClosedCasesPage}
                            disabled={isFetchingNextClosedCasesPage}
                        >
                            {L('LØN_AFSLUTTET_SAGER_INDLÆS_FLERE')}
                        </Button>
                    </div>
                )}
        </>
    )
}

export default ClosedCases
